import { GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import React, { useEffect, useMemo, useState } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { CardholderSelection } from 'src/app/CardFeedDetails/components/AssignCardholderDropdown'
import * as style from 'src/app/CardFeedDetails/components/CardFeedCardsTable.style'
import { buildCardFeedColumns } from 'src/app/CardFeedDetails/components/cardFeedColumns'
import CardFeedFailedEmptyState from 'src/app/CardFeedDetails/components/CardFeedFailedEmptyState'
import CardFeedPendingEmptyState from 'src/app/CardFeedDetails/components/CardFeedPendingEmptyState'
import CardSelectionSnackbar from 'src/app/CardFeedDetails/components/CardSelectionSnackbar'
import CardStatusFilter from 'src/app/CardFeedDetails/components/CardStatusFilter'
import { resolveSelectedCards } from 'src/app/CardFeedDetails/utils/resolveSelectedCards'
import {
  CardFeedCardsView,
  CardFeedSortField,
  selectVisibleCards,
} from 'src/app/CardFeedDetails/utils/selectVisibleCards'
import Box from 'src/components/Box/Box'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import { useTranslate } from 'src/localization/Trns'
import {
  CardFeedCard,
  CardFeedCardStatus,
} from 'src/queries/cardFeeds/cardFeeds.models'
import useQueryCardFeedCards from 'src/queries/cardFeeds/domain/useQueryCardFeedCards'

export interface CardFeedCardsTableProps {
  feedId: string
  /**
   * Called with the cards the user wants to assign an account owner to —
   * fires when the user clicks the snackbar's primary action with one or more
   * unassigned cards selected. Required because the snackbar's primary action
   * is part of the table's bulk-action contract; a hypothetical read-only
   * caller would need to also suppress the snackbar, not just omit the
   * callback.
   */
  onAssignRequest: (cards: CardFeedCard[]) => void
  /**
   * Fires when the user picks a cardholder from a single row's inline
   * dropdown — bypasses the bulk flyout and goes straight to the parent's
   * confirmation modal with both the card and the selected recipient.
   */
  onAssignFromRow: (card: CardFeedCard, selection: CardholderSelection) => void
  /** Fires when the user picks Reinvite from a PENDING row's action menu. */
  onReinvite: (card: CardFeedCard) => void
  /** Fires when the user picks Unassign from a PENDING row's action menu. */
  onUnassign: (card: CardFeedCard) => void
  /**
   * Incremented by the parent after a successful assignment to clear the
   * current selection — the assigned cards transition out of UNASSIGNED, so
   * the bulk selection no longer applies.
   */
  resetSelectionSignal?: number
  /**
   * The feed is connected (ACTIVE). Only an active feed can take assignments,
   * so when false the grid is read-only: row checkboxes and the row-level
   * assign dropdown are suppressed.
   */
  isActive?: boolean
  /**
   * The feed is SUBMITTED (pending) — the bank hasn't activated it yet, so it
   * has no cards and nothing to search or filter. Replaces the entire table
   * (toolbar and grid included) with the illustrated "connecting with your
   * bank" empty state.
   */
  isPending?: boolean
  /**
   * The feed is FAILED — it never connected, so there are no cards. Replaces
   * the toolbar and grid with the illustrated "error connecting" empty state
   * and skips the cards query.
   */
  isFailed?: boolean
}

const GRID_HEIGHT = 560
const SORTABLE_FIELDS: CardFeedSortField[] = [
  'cardName',
  'cardHolder',
  'status',
  'dateAdded',
]

// MUI X 8 models row selection as `{ type, ids }`, not a plain id array. A
// factory (not a shared constant) guarantees every init/reset gets a fresh
// model with its own Set, so no instance or reset can mutate another's state.
const createEmptySelection = (): GridRowSelectionModel => ({
  type: 'include',
  ids: new Set(),
})

const CardFeedCardsTable: React.FC<CardFeedCardsTableProps> = ({
  feedId,
  onAssignRequest,
  onAssignFromRow,
  onReinvite,
  onUnassign,
  resetSelectionSignal,
  isActive = false,
  isPending,
  isFailed,
}) => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  // Pending and failed feeds have no cards — skip the request and show the
  // appropriate empty state instead.
  const { cards, isTruncated, isLoading, isError } = useQueryCardFeedCards(
    feedId,
    !isPending && !isFailed,
  )

  const [searchValue, setSearchValue] = useState('')
  const [statuses, setStatuses] = useState<CardFeedCardStatus[]>([])
  // Default to status ascending so UNASSIGNED (actionable) cards surface at
  // the top, with card name as the tie-break within each group (EX-39728).
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'status', sort: 'asc' },
  ])
  const [selectionModel, setSelectionModel] =
    useState<GridRowSelectionModel>(createEmptySelection)

  useEffect(() => setSelectionModel(createEmptySelection()), [feedId])
  useEffect(() => {
    if (resetSelectionSignal === undefined) return
    setSelectionModel(createEmptySelection())
  }, [resetSelectionSignal])

  const view: CardFeedCardsView = useMemo(() => {
    const sort = sortModel[0]
    const sortField =
      sort && SORTABLE_FIELDS.includes(sort.field as CardFeedSortField)
        ? (sort.field as CardFeedSortField)
        : undefined
    return {
      search: searchValue,
      statuses,
      sortField,
      sortDirection: sort?.sort === 'desc' ? 'DESC' : 'ASC',
    }
  }, [searchValue, statuses, sortModel])

  const visibleCards = useMemo(
    () => selectVisibleCards(cards, view),
    [cards, view],
  )

  const columns = useMemo(
    () =>
      buildCardFeedColumns({
        translate,
        onAssignFromRow,
        onReinvite,
        onUnassign,
        isActive,
      }),
    [translate, onAssignFromRow, onReinvite, onUnassign, isActive],
  )

  const isFiltering = searchValue.trim().length > 0 || statuses.length > 0

  // Resolve against visibleCards (the rows in the grid), not the full feed, so
  // "select all" while a filter is active never pulls in cards the user can't
  // see. See resolveSelectedCards for the include/exclude model handling.
  const selectedCards = useMemo(
    () => resolveSelectedCards(visibleCards, selectionModel),
    [visibleCards, selectionModel],
  )

  const handleAssignSelected = (): void => {
    if (selectedCards.length === 0) return
    onAssignRequest(selectedCards)
  }

  const handleSortModelChange = (newModel: GridSortModel): void => {
    setSortModel(newModel)
    const sort = newModel[0]
    if (sort) {
      analytics.track('card feed details', {
        action: 'sort applied',
        sort_by: sort.field,
        sort_direction: sort.sort === 'desc' ? 'desc' : 'asc',
      })
    }
  }

  const handleRowSelectionModelChange = (
    newModel: GridRowSelectionModel,
  ): void => {
    // Compare resolved selectable counts rather than trusting the model type.
    // MUI keeps `type: 'exclude'` after a header select-all, so a subsequent row
    // *deselect* arrives as an exclude model with a growing excluded id set —
    // treating every exclude model as "select all" would emit a bogus
    // `card selected` event for deselects and other shrink operations. Firing
    // only when the resolved count grows avoids that.
    const prevCount = selectedCards.length
    const newCount = resolveSelectedCards(visibleCards, newModel).length
    setSelectionModel(newModel)

    if (newCount <= prevCount) return

    // Distinguish the header "select all" from a single row toggle by how many
    // rows were added at once. MUI represents select-all as either an empty
    // `exclude` model (paginated / server data) or an `include` model holding
    // every id (the in-memory case) — a bulk add either way, vs. a single row
    // checkbox that adds exactly one.
    const isSelectAll =
      (newModel.type === 'exclude' && newModel.ids.size === 0) ||
      newCount - prevCount > 1
    analytics.track('card feed details', {
      action: 'card selected',
      selection: isSelectAll ? 'all' : 'single',
    })
  }

  const noRowsOverlay = (): React.ReactElement => (
    <div
      css={style.overlay}
      data-testid={
        isFiltering ? 'card-feed-cards-no-match' : 'card-feed-cards-empty'
      }
    >
      {isFiltering
        ? translate('No cards match your search or filters.')
        : translate(
            'No cards yet — your bank will activate this feed shortly.',
          )}
    </div>
  )

  // A pending feed has no cards and nothing to search or filter yet, so drop
  // the toolbar and grid entirely and show only the "connecting with your bank"
  // empty state.
  if (isPending) {
    return (
      <Box
        noPadding
        outlineBorder
        css={style.tableCard}
        data-testid="card-feed-cards-table"
      >
        <CardFeedPendingEmptyState />
      </Box>
    )
  }

  // A failed feed never connected and has no cards, so drop the toolbar and
  // grid and show only the "error connecting" empty state.
  if (isFailed) {
    return (
      <Box
        noPadding
        outlineBorder
        css={style.tableCard}
        data-testid="card-feed-cards-table"
      >
        <CardFeedFailedEmptyState />
      </Box>
    )
  }

  return (
    <Box
      noPadding
      outlineBorder
      css={style.tableCard}
      data-testid="card-feed-cards-table"
    >
      <div css={style.toolbar}>
        <div css={style.search}>
          <TextInput
            value={searchValue}
            onChange={(e): void => setSearchValue(e.target.value)}
            placeholder={translate('Search...')}
            aria-label={translate('Search cards')}
            testId="card-feed-cards-search"
          />
        </div>
        <CardStatusFilter statuses={statuses} onChange={setStatuses} />
      </div>

      {isError ? (
        <div css={style.overlay}>
          <span role="alert" data-testid="card-feed-cards-error">
            {translate(
              "We couldn't load the cards for this feed. Please try again.",
            )}
          </span>
        </div>
      ) : isLoading ? (
        <div
          css={style.overlay}
          role="status"
          aria-live="polite"
          data-testid="card-feed-cards-loading"
        >
          {translate('Loading...')}
        </div>
      ) : (
        <>
          {isTruncated && (
            <div
              css={style.truncationNotice}
              data-testid="card-feed-cards-truncation"
            >
              {translate(
                'Showing first 100 cards. Sorting and filtering apply to these only.',
              )}
            </div>
          )}
          <div css={style.gridWrap}>
            <DataGrid<CardFeedCardsView, CardFeedCard>
              height={GRID_HEIGHT}
              rows={visibleCards}
              columns={columns}
              filtersBody={view}
              rowHeight={DataGridRowHeight.Default}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              // Only an active feed can take assignments, so non-active feeds
              // render read-only — no row checkboxes (and the columns drop the
              // row-level assign dropdown).
              checkboxSelection={isActive}
              keepNonExistentRowsSelected
              isRowSelectable={(params): boolean =>
                isActive && params.row.status === 'UNASSIGNED'
              }
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={handleRowSelectionModelChange}
              disableColumnFilter
              disableRowSelectionOnClick
              slots={{ noRowsOverlay }}
            />
          </div>
        </>
      )}

      <CardSelectionSnackbar
        selectedCount={selectedCards.length}
        onAssign={handleAssignSelected}
        onClose={(): void => setSelectionModel(createEmptySelection())}
      />
    </Box>
  )
}

export default CardFeedCardsTable
